import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { makeStyles } from 'tss-react/mui'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { Controller, useForm } from 'react-hook-form'
import type { FieldValues } from 'react-hook-form'
import addDays from 'date-fns/addDays'
import isEqual from 'lodash/isEqual'
import ReactRouterPrompt from 'react-router-prompt'

import type { Theme } from '@mui/material/styles'
import type { DepartmentListItemProps } from 'types/common'
import type { PreReservationForm } from 'types/med'
import { Button, MenuItem, Select, Switch } from '@mui/material'

import { StackItem } from 'ui/layout/StackItem'
import { StackItemTextField } from 'ui/shorthands/StackItemTextField'
import Section from 'ui/layout/Section'
import { RequiredLabel } from 'ui/shorthands/Labels'
import validationSchema from 'ui/shorthands/validationSchema'
import ReservationDateSelect from 'ui/utils/ReservationDateSelect'
import FutureReservationFrames from 'ui/organisms/FutureReservationFrames'
import { useStylesInnerAdminContentWrapper } from 'ui/layout/AdminContentWrapper'
import { jstNewDate } from 'ui/utils/jstDatetime'
import Modal from 'ui/layout/Modal'
import { ConfirmContent } from 'ui/partials/ConfirmContent'

const useStyles = makeStyles()((theme: Theme) => ({
  select: {
    maxWidth: '100%',
    minWidth: theme.spacing(41),
    textOverflow: 'ellipsis',
  },
  switchWrapper: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: theme.spacing(0),
    marginTop: theme.spacing(-1),
  },
}))

type PreReservationFormProps = {
  /** 全ての診療科目概要の配列 */
  departments: DepartmentListItemProps[]
  /** 「送信」ボタンがクリックされた時のイベントハンドラ */
  onSubmit: (data: PreReservationForm) => Promise<void>
  /** キャンセルボタンが実行された時のイベントハンドラ */
  onDiscard: () => void
}

const PreReservationCreate: React.FC<PreReservationFormProps> = ({
  departments,
  onSubmit,
  onDiscard,
}: PreReservationFormProps) => {
  const { t } = useTranslation()
  const { classes } = useStyles()
  const layoutClasses = useStylesInnerAdminContentWrapper().classes

  const [contactType, setContactType] = useState<
    'applicant_phone' | 'applicant_email'
  >('applicant_phone')

  // NOTE: 診療科目が一つの時は選択済みにする
  let defaultDepartmentId: number | undefined = undefined
  const filtered = departments.filter((d) => d.is_open)
  if (filtered?.length === 1) {
    defaultDepartmentId = filtered[0].id
  }

  const defaultInput: PreReservationForm = {
    department_id: defaultDepartmentId,
    provisional_reservation_datetime_start: '',
    provisional_reservation_datetime_end: '',
    applicant_name: '',
    applicant_phone: undefined,
    applicant_email: undefined,
  }

  const schema = yup.object().shape({
    applicant_phone: validationSchema
      .textRequired()
      .test(
        'wrongType',
        t('validation.正しい形式にしてください'),
        function (string) {
          const phoneSchema = yup.object().shape({
            string: validationSchema.phone(),
          })
          const emailSchema = yup.object().shape({
            string: validationSchema.email(),
          })
          return new Promise((resolve) => {
            phoneSchema
              .isValid({ string })
              .then((result) => {
                result
                  ? resolve(true)
                  : emailSchema
                      .isValid({ string })
                      .then(resolve)
                      .catch(() => resolve(false))
              })
              .catch(() => resolve(false))
          })
        }
      ),
    department_id: yup.number(),
    provisional_reservation_datetime_start: yup.string(),
    provisional_reservation_datetime_end: yup.string(),
    applicant_name: yup.string(),
  })

  const {
    handleSubmit,
    control,
    setValue,
    formState: { errors, isValid },
    watch,
    trigger,
  } = useForm<FieldValues>({
    defaultValues: defaultInput,
    resolver: yupResolver(schema),
    mode: 'onChange',
  })

  const watchAll = watch()

  const watchDatetime = watch('provisional_reservation_datetime_start')
  const watchContact = watch('applicant_phone')
  const watchDepartmentId = watch('department_id')

  const [useSelectDate, setUseSelectDate] = useState<boolean>(false)
  const [selectedDate, setSelectedDate] = useState<Date>(jstNewDate)
  const [datetimeRequired, setDatetimeRequired] = useState<boolean>(false)
  const [finished, setFinished] = useState<boolean>(false)
  const [showDiscardModal, setShowDiscardModal] = useState<boolean>(false)

  const [sending, setSending] = useState<boolean>(false)

  const resetStartEnd = () => {
    setValue('provisional_reservation_datetime_start', '')
    setValue('provisional_reservation_datetime_end', '')
  }

  const onSelectDateTime: (data: {
    provisional_reservation_datetime_start: string
    provisional_reservation_datetime_end: string
  }) => void = ({
    provisional_reservation_datetime_start,
    provisional_reservation_datetime_end,
  }) => {
    setValue(
      'provisional_reservation_datetime_start',
      provisional_reservation_datetime_start
    )
    setValue(
      'provisional_reservation_datetime_end',
      provisional_reservation_datetime_end
    )
    trigger([
      'provisional_reservation_datetime_start',
      'provisional_reservation_datetime_end',
    ])
  }

  useEffect(() => {
    if (!useSelectDate) {
      setSelectedDate(jstNewDate)
      resetStartEnd()
    }
    setDatetimeRequired(useSelectDate && !watchDatetime)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useSelectDate, watchDatetime])

  useEffect(() => {
    resetStartEnd()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate])

  useEffect(() => {
    setContactType(
      // NOTE: yupのバリデーションが通っていること前提なので"@"のみで判定
      watchContact && watchContact.match(/@/)
        ? 'applicant_email'
        : 'applicant_phone'
    )
  }, [watchContact])

  const submitHandler = (data: PreReservationForm | any) => {
    setSending(true)
    let sendData = { ...data }
    if (contactType === 'applicant_email') {
      sendData['applicant_email'] = data.applicant_phone
      sendData['applicant_phone'] = undefined
    }
    const result = onSubmit(sendData)
    result
      .then(() => setFinished(true))
      .finally(() => {
        setSending(false)
      })
  }

  const isBlocked = !finished && !isEqual(defaultInput, watchAll)

  const discardModal = ({
    open,
    onCancel,
    onConfirm,
  }: {
    open: boolean
    onCancel: () => void
    onConfirm: () => void
  }) => (
    <Modal open={open} handleClose={() => onCancel()}>
      <ConfirmContent
        onCancel={() => {
          onCancel()
        }}
        onSubmit={() => {
          onConfirm()
        }}
        title={t('編集内容が破棄されます。よろしいですか？')}
        submitLabel={t('破棄する')}
      />
    </Modal>
  )

  return (
    <>
      <ReactRouterPrompt
        // NOTE: react-router-dom v6でPromptコンポーネントが削除されたためこの実装になった
        // 代替として使ったこのreact-router-promptのreadmeで
        // react-router-dom >= 6.7 and can be used only with data routers
        // とあり、routeの記法をdataAPI形式にしないと動かないので
        // 現状react-router-dom v6.3,react-router-prompt v0.3 のバージョンになった
        when={isBlocked}
      >
        {({ isActive, onConfirm, onCancel }) => {
          // NOTE: 画面遷移が発生した時の確認モーダル
          if (isActive) {
            return discardModal({
              open: isBlocked,
              onCancel: () => onCancel(null), // ReactRouterPromptがinactiveになる
              onConfirm: () => onConfirm(null), // ブロックされていた画面遷移が実行される
            })
          } else {
            // NOTE: キャンセルボタンをクリックした時の確認モーダル
            return discardModal({
              open: showDiscardModal,
              onCancel: () => {
                setShowDiscardModal(false)
              },
              onConfirm: () => {
                setShowDiscardModal(false)
                setFinished(true)
                onDiscard()
              },
            })
          }
        }}
      </ReactRouterPrompt>

      <form
        onSubmit={handleSubmit(submitHandler)}
        className={layoutClasses.root}
      >
        <div className={layoutClasses.main}>
          <div className={layoutClasses.content}>
            <Section>
              <StackItem
                label={
                  <RequiredLabel optional={'optional'}>
                    {t('受付科目')}
                  </RequiredLabel>
                }
              >
                <Controller
                  name={'department_id'}
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      variant="outlined"
                      displayEmpty
                      className={classes.select}
                    >
                      <MenuItem>{t('未指定')}</MenuItem>
                      {departments
                        .filter((d) => d.is_open)
                        .map((d) => (
                          <MenuItem key={d.name} value={d.id}>
                            {d.name}
                          </MenuItem>
                        ))}
                    </Select>
                  )}
                />
              </StackItem>
              <StackItem
                label={
                  <RequiredLabel optional={'optional'}>
                    {t('日時')}
                  </RequiredLabel>
                }
                alignTop
              >
                <div className={classes.switchWrapper}>
                  <div>{t('未指定')}</div>
                  <Switch
                    color="primary"
                    checked={useSelectDate} // NOTE: Zoom接続がなければ強制的にONの表示にする
                    inputProps={{ 'aria-label': 'primary checkbox' }}
                    onChange={() => {
                      setUseSelectDate((prev) => !prev)
                    }}
                  />
                  <div>{t('指定')}</div>
                </div>
              </StackItem>
              {useSelectDate && (
                <>
                  <ReservationDateSelect
                    selectedDate={selectedDate}
                    onSelect={setSelectedDate}
                    minDate={jstNewDate}
                    maxDate={addDays(jstNewDate, 12 * 7 - 1)}
                  />
                  <FutureReservationFrames
                    unit={
                      departments.find(({ id }) => id === watchDepartmentId)
                        ?.reservation_frame_period_minutes || 30
                    }
                    date={selectedDate}
                    onSelect={(data) =>
                      onSelectDateTime({
                        provisional_reservation_datetime_start:
                          data.start_datetime,
                        provisional_reservation_datetime_end: data.end_datetime,
                      })
                    }
                  />
                </>
              )}
              <Controller
                render={({ field }) => <input {...field} type="hidden" />}
                name="provisional_reservation_datetime_start"
                control={control}
              />
              <Controller
                render={({ field }) => <input {...field} type="hidden" />}
                name="provisional_reservation_datetime_end"
                control={control}
              />
              <StackItemTextField
                inputMinWidth={326}
                label={
                  <RequiredLabel optional={'optional'}>
                    {t('氏名')}
                  </RequiredLabel>
                }
                description={t(
                  '仮予約管理にのみ使用します。送信されるメッセージには表示されません。'
                )}
                errorMessage={errors.applicant_name?.message}
                placeholder={t('placeholder.ヤマダタロウ')}
                name={'applicant_name'}
                control={control}
                alignTop
              />
              <StackItemTextField
                inputMinWidth={326}
                label={t('電話番号またはメールアドレス')}
                errorMessage={errors.applicant_phone?.message}
                description={t(
                  '予約フォームのリンクがSMSまたはメール送信されます。電話を受けた段階では仮予約状態であることを予約者にご説明ください。各社携帯キャリアのメールアドレス（@docomo.ne.jp,@softbank.ne.jp,@ezweb.ne.jpなど）は利用できません。'
                )}
                placeholder={t('電話番号またはメールアドレス')}
                name={'applicant_phone'}
                control={control}
                alignTop
              />
            </Section>
          </div>
          <div className={layoutClasses.controlBar}>
            <div>
              <Button
                onClick={() => {
                  if (isBlocked) {
                    setShowDiscardModal(true)
                  } else {
                    onDiscard()
                  }
                }}
                color={'primary'}
              >
                {t('キャンセル')}
              </Button>
            </div>
            <div>
              <Button
                variant="contained"
                color="primary"
                type="submit"
                disabled={!isValid || datetimeRequired || sending}
              >
                {t('送信')}
              </Button>
            </div>
          </div>
        </div>
      </form>
    </>
  )
}

export default PreReservationCreate
